import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ClearincomingCall } from "../../../redux/consultant/consultantAction";
import { useSelector } from "react-redux";
import { FaPhoneAlt, FaTimes, FaUser } from "react-icons/fa";
import { useSocket } from "../../../utils/Socket.Provider";
import { useGetConsultantProfileQuery } from "../../../redux/rtk/apiRtk";

const Incomingcall = () => {
  const dispatch = useDispatch();
  const { socket } = useSocket();
  const params = useParams();
  const navigate = useNavigate();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const { data: ConsultantProfileData } = useGetConsultantProfileQuery("");

  const handleRejectCall = () => {
    setIsRejecting(true);

    // Only emit if socket and consultant data are available
    if (socket && ConsultantProfileData?._id) {
      socket.emit("endcall", {
        toUser: params.id,
        fromUser: ConsultantProfileData._id,
        consultantid: ConsultantProfileData._id,
        user: params.id,
        meetingId: params.meetingid,
      });
    }

    // Show rejecting state for a moment before navigating
    setTimeout(() => {
      dispatch(ClearincomingCall());
      navigate("/profile");
    }, 1500);
  };

  const handleAcceptCall = () => {
    setIsAccepting(true);

    // Request microphone permissions before accepting call
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        // Permissions granted, accept the call
        if (socket && ConsultantProfileData?._id) {
          socket.emit("accept-call", {
            toUser: params.id,
            fromUser: ConsultantProfileData._id,
            perMinuteCoin: 0,
            consultantid: ConsultantProfileData._id,
            user: params.id,
            meetingId: params.meetingid,
          });
        }

        // Navigate to call screen after a short delay
        setTimeout(() => {
          navigate(`/oncall/${params.id}/${params.meetingid}`);
        }, 1000);
      })
      .catch((error) => {
        // Handle permission denied
        console.error("Microphone permission denied:", error);
        setIsAccepting(false);
        alert(
          "Microphone access is required to accept calls. Please allow microphone access and try again."
        );
      });
  };

  const callData = useSelector((state) => state?.consultant?.incomingCall);

  useEffect(() => {
    if (!callData) {
      navigate("/profile");
    }
  }, [callData, navigate]);

  useEffect(() => {
    socket.on("endcall", () => {
      setTimeout(() => {
        navigate("/");
      }, 1000);
    });

    return () => {
      socket.off("endcall");
    };
  }, [socket, navigate]);

  if (!callData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100 backdrop-blur-sm flex items-center justify-center z-[9999]">
      {/* Background decorative elements - lighter theme */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-[#A40165]/5 blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#A40165]/5 blur-3xl"></div>
        <div className="absolute top-1/3 right-1/3 w-40 h-40 rounded-full bg-purple-200/20 blur-2xl"></div>
      </div>

      <div className="w-full max-w-md mx-4 relative">
        {/* Glass-like card with light theme styling */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl overflow-hidden shadow-xl border border-[#A40165]/20">
          {/* Top wave decoration with enhanced gradient */}
          <div className="h-16 bg-gradient-to-r from-[#A40165] via-[#8A0154] to-[#6A0165] relative overflow-hidden">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-white opacity-10"></div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full h-16 animate-pulse"
                  style={{
                    opacity: 0.1 + i * 0.1,
                    animationDelay: `${i * 0.2}s`,
                    borderRadius: "100%",
                    transform: `scale(${1 + i * 0.2})`,
                    top: `${10 + i * 5}px`,
                  }}
                ></div>
              ))}
            </div>
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <p className="text-white text-sm font-medium tracking-wider uppercase">
                Incoming Call
              </p>
            </div>
          </div>

          {/* Caller profile section with light theme styling */}
          <div className="relative flex flex-col items-center px-8 pt-12 pb-6 bg-gradient-to-b from-transparent via-[#A40165]/5 to-transparent">
            {/* Pulsating rings animation - properly centered */}
            <div className="relative mb-4 mt-2">
              {/* Centered rings animation */}
              <div className="absolute inset-0 flex items-center justify-center">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full border-2 border-[#A40165] opacity-0"
                    style={{
                      width: `${100 + i * 20}px`,
                      height: `${100 + i * 20}px`,
                      animation: `ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  ></div>
                ))}
              </div>

              {/* Caller image with light theme styling */}
              <div className="relative z-10">
                <div className="absolute inset-0 rounded-full bg-[#A40165]/20 blur-md transform scale-110"></div>
                {callData?.profilePic ? (
                  <img
                    src={callData.profilePic}
                    alt={callData.name}
                    className="relative w-28 h-28 rounded-full object-cover border-4 border-[#A40165] shadow-lg shadow-[#A40165]/20 p-1 bg-white"
                  />
                ) : (
                  <div className="relative w-28 h-28 rounded-full bg-white border-4 border-[#A40165] shadow-lg shadow-[#A40165]/20 flex items-center justify-center">
                    <FaUser className="text-gray-400 text-4xl" />
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-r from-green-500 to-green-400 rounded-full border-2 border-white animate-pulse flex items-center justify-center">
                  <FaPhoneAlt className="text-white text-xs" />
                </div>
              </div>
            </div>

            {/* Caller info with light theme styling */}
            <h2 className="text-3xl font-bold text-gray-800 mb-1 relative">
              {callData?.name || "Unknown"}
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#A40165]/50 to-transparent"></span>
            </h2>
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-block w-2 h-2 bg-gradient-to-r from-[#A40165] to-[#8A0154] rounded-full animate-pulse"></span>
              <p className="text-[#A40165] font-medium text-sm">
                Calling you now
              </p>
            </div>

            {/* Animated dots with light theme styling */}
            <div className="flex gap-3 mb-8">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-gray-400 to-gray-300"
                  style={{
                    animation: "bounce 1s infinite",
                    animationDelay: `${i * 0.2}s`,
                  }}
                ></div>
              ))}
            </div>
          </div>

          {/* Call actions with light theme styling */}
          <div className="bg-gray-100 px-8 py-6 flex justify-between items-center border-t border-[#A40165]/10">
            {/* Decline button with light theme styling */}
            <button
              onClick={handleRejectCall}
              disabled={isRejecting || isAccepting}
              className="group flex flex-col items-center transition-all duration-300"
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md transition-all duration-300 
                ${
                  isRejecting
                    ? "bg-gray-300"
                    : "bg-gradient-to-br from-red-500 to-red-600 hover:shadow-red-400/30 hover:scale-105"
                }`}
              >
                <FaTimes size={24} className="text-white" />
              </div>
              <span className="text-gray-600 text-sm mt-2 group-hover:text-gray-800">
                {isRejecting ? "Declining..." : "Decline"}
              </span>
            </button>

            {/* Accept button with light theme styling */}
            <button
              onClick={handleAcceptCall}
              disabled={isAccepting || isRejecting}
              className="group flex flex-col items-center transition-all duration-300"
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md transition-all duration-300
                ${
                  isAccepting
                    ? "bg-gray-300"
                    : "bg-gradient-to-br from-[#A40165] to-[#8A0154] hover:shadow-[#A40165]/30 hover:scale-105"
                }`}
              >
                <FaPhoneAlt size={22} className="text-white" />
              </div>
              <span className="text-gray-600 text-sm mt-2 group-hover:text-gray-800">
                {isAccepting ? "Connecting..." : "Accept"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Incomingcall;
